import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { 
  CheckCircle, ArrowRight, Phone, Clock, Users, Gift
} from 'lucide-react';

const pricingOptions = [
  {
    title: "Éveil Musical",
    subtitle: "3-8 ans",
    description: "Atelier collectif ou individuel pour découvrir la musique en douceur",
    features: [
      "Séances de 30 à 45 minutes",
      "Collectif (max 7-8 enfants) ou individuel",
      "Instruments adaptés fournis",
      "Comptines et jeux musicaux"
    ],
    price: "Sur demande",
    note: "Tarif adapté selon la formule choisie",
    color: "coral",
    popular: false
  },
  {
    title: "Cours Instrument",
    subtitle: "Piano, Guitare ou Chant",
    description: "Apprentissage personnalisé pour progresser à votre rythme",
    features: [
      "Cours de 30, 45 ou 60 min",
      "Individuel ou collectif",
      "Programme sur mesure",
      "Répertoire à votre goût",
      "Préparation examens (option)"
    ],
    price: "Sur demande",
    note: "Tarif selon durée et engagement",
    color: "burgundy",
    popular: true
  },
  {
    title: "Atelier Bébé-Parent",
    subtitle: "Dès 3 mois",
    description: "Moment privilégié de partage et d'éveil sensoriel",
    features: [
      "Séances de 30 à 45 minutes",
      "Parent + enfant ensemble",
      "Approche sensorielle globale",
      "Matériel adapté aux tout-petits"
    ],
    price: "Sur demande",
    note: "Formule à la séance ou abonnement",
    color: "teal",
    popular: false
  }
];

const advantages = [
  {
    icon: Gift,
    title: "Cours d'essai offert",
    description: "Découvrez gratuitement notre méthode avant tout engagement"
  },
  {
    icon: Clock,
    title: "Horaires flexibles",
    description: "Cours en journée, soir, mercredi et samedi selon vos disponibilités"
  },
  {
    icon: Users,
    title: "Suivi personnalisé",
    description: "Chaque élève bénéficie d'un programme adapté à ses objectifs"
  }
];

const PricingCard = ({ option }) => {
  const colorClasses = {
    burgundy: {
      bg: 'bg-burgundy',
      bgLight: 'bg-burgundy/10',
      text: 'text-burgundy',
      border: 'border-burgundy'
    },
    coral: {
      bg: 'bg-coral',
      bgLight: 'bg-coral/10',
      text: 'text-coral',
      border: 'border-coral'
    },
    teal: {
      bg: 'bg-teal',
      bgLight: 'bg-teal/10',
      text: 'text-teal',
      border: 'border-teal'
    }
  };

  const colors = colorClasses[option.color];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={`relative bg-white rounded-3xl shadow-lg overflow-hidden ${option.popular ? 'ring-2 ring-burgundy' : ''}`}
      data-testid={`pricing-card-${option.title.toLowerCase().replace(/\s/g, '-')}`}
    >
      {option.popular && (
        <div className="absolute top-0 right-0 bg-burgundy text-white px-4 py-1 text-sm font-semibold rounded-bl-xl">
          Populaire
        </div>
      )}

      <div className="p-8">
        <div className={`inline-block px-3 py-1 ${colors.bgLight} ${colors.text} rounded-full text-sm font-semibold mb-3`}>
          {option.subtitle}
        </div>
        <h3 className="font-dancing text-3xl text-burgundy mb-2">{option.title}</h3>
        <p className="text-gray-600 text-sm mb-6">{option.description}</p>

        <div className="mb-6">
          <p className={`text-2xl font-bold ${colors.text}`}>{option.price}</p>
          <p className="text-gray-500 text-sm">{option.note}</p>
        </div>

        <ul className="space-y-3 mb-8">
          {option.features.map((feature) => (
            <li key={feature} className="flex items-start gap-3 text-gray-700 text-sm">
              <CheckCircle className={`w-5 h-5 ${colors.text} flex-shrink-0`} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <Link
          to="/contact"
          className={`block w-full py-3 ${option.popular ? colors.bg + ' text-white hover:opacity-90' : 'border-2 ' + colors.border + ' ' + colors.text + ' hover:' + colors.bg + ' hover:text-white'} rounded-full font-semibold text-center transition-all`}
          data-testid={`pricing-cta-${option.title.toLowerCase().replace(/\s/g, '-')}`}
        >
          Demander les tarifs
        </Link>
      </div>
    </motion.div>
  );
};

export default function PricingPage() {
  return (
    <div className="min-h-screen pt-20" data-testid="pricing-page">
      <Helmet>
        <title>Tarifs Cours de Musique à Périgueux | Piano, Guitare, Chant, Éveil Musical</title>
        <meta name="description" content="Découvrez les tarifs des cours de piano, guitare, chant et éveil musical à Périgueux. Premier cours d'essai gratuit. Formules adaptées à tous les budgets." />
        <link rel="canonical" href="https://ecoledemusiqueperigueux.fr/tarifs" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-cream to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="inline-block px-4 py-2 bg-burgundy/10 text-burgundy rounded-full text-sm font-semibold mb-4">
              Tarifs Cours de Musique à Périgueux
            </span>
            <h1 className="font-dancing text-5xl sm:text-6xl text-burgundy mb-6">
              Tarifs cours de piano, guitare et chant
            </h1>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto font-nunito">
              Découvrez nos formules de cours de musique à Périgueux. 
              Premier cours d'essai gratuit et sans engagement !
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pricingOptions.map((option) => (
              <PricingCard key={option.title} option={option} />
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-dancing text-4xl text-burgundy mb-4">
              Les avantages École de Musique Pour Tous
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {advantages.map((advantage) => {
              const Icon = advantage.icon;
              return (
                <motion.div
                  key={advantage.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="text-center p-6"
                >
                  <div className="w-16 h-16 bg-coral/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-coral" />
                  </div>
                  <h3 className="font-nunito font-bold text-lg text-gray-800 mb-2">{advantage.title}</h3>
                  <p className="text-gray-600 text-sm">{advantage.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info Box */}
      <section className="py-16 bg-cream">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl shadow-lg p-8 text-center">
            <h2 className="font-dancing text-3xl text-burgundy mb-4">
              Pourquoi "sur demande" ?
            </h2>
            <p className="text-gray-600 mb-6 font-nunito leading-relaxed">
              Chaque élève est unique et mérite une attention personnalisée. 
              Le tarif dépend de plusieurs facteurs : la durée du cours, la fréquence, 
              l'engagement (ponctuel ou annuel), et le type de formule choisi. 
              Contactez-nous pour recevoir un devis personnalisé adapté à votre situation.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 bg-burgundy text-white rounded-full font-semibold hover:bg-burgundy-600 transition-all"
                data-testid="pricing-contact-btn"
              >
                Demander un devis gratuit
                <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="tel:+33649523778"
                className="inline-flex items-center gap-2 px-6 py-3 border-2 border-burgundy text-burgundy rounded-full font-semibold hover:bg-burgundy hover:text-white transition-all"
                data-testid="pricing-phone-btn"
              >
                <Phone className="w-5 h-5" />
                06 49 52 37 78
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Engagement Options */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-dancing text-4xl text-burgundy mb-4">
              Formules d'engagement
            </h2>
            <p className="text-gray-600 font-nunito">Des options flexibles pour s'adapter à votre rythme de vie</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-teal/5 rounded-3xl p-8 border border-teal/20"
            >
              <h3 className="font-nunito font-bold text-xl text-teal mb-4">Engagement ponctuel</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-teal" />
                  <span>Flexibilité maximale</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-teal" />
                  <span>Idéal pour découvrir</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-teal" />
                  <span>Sans engagement de durée</span>
                </li>
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-burgundy/5 rounded-3xl p-8 border border-burgundy/20"
            >
              <h3 className="font-nunito font-bold text-xl text-burgundy mb-4">Engagement annuel</h3>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-burgundy" />
                  <span>Tarif préférentiel</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-burgundy" />
                  <span>Progression optimale</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-burgundy" />
                  <span>Créneau garanti</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Informations complémentaires SEO */}
      <section className="py-16 bg-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-dancing text-3xl text-burgundy mb-6 text-center">
              Pourquoi choisir notre école de musique à Périgueux ?
            </h2>
            <div className="bg-white rounded-3xl p-8 shadow-sm">
              <div className="prose prose-gray max-w-none font-nunito text-gray-700 space-y-4">
                <p>
                  L'<strong>École de Musique Pour Tous</strong> propose des <strong>cours de piano à Périgueux</strong>, 
                  des <strong>cours de guitare</strong>, de <strong>chant</strong> et d'<strong>éveil musical</strong> 
                  pour tous les âges et tous les niveaux. Située au 2 Rue Ludovic Trarieux dans le quartier Clos Chassaing, 
                  notre école accueille les élèves dans un cadre chaleureux et bienveillant.
                </p>
                <p>
                  Nos tarifs de <strong>cours de musique à Périgueux</strong> sont étudiés pour être accessibles à tous. 
                  Que vous souhaitiez débuter le <strong>piano</strong>, apprendre la <strong>guitare acoustique</strong>, 
                  développer votre voix avec des <strong>cours de chant</strong>, ou initier votre enfant à la musique 
                  avec l'<strong>éveil musical</strong>, nous avons une formule adaptée à votre projet.
                </p>
                <p>
                  Charlotte Naboulet et son équipe de professeurs diplômés s'adaptent à chaque élève avec une 
                  pédagogie personnalisée. Le premier cours d'essai est gratuit et sans engagement, pour vous permettre 
                  de découvrir notre approche et de rencontrer votre futur professeur.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-burgundy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-dancing text-4xl sm:text-5xl mb-6">
              Prêt(e) à vous lancer ?
            </h2>
            <p className="text-white/80 text-lg mb-8 font-nunito">
              Contactez notre équipe pour discuter de votre projet musical et obtenir un devis personnalisé.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-burgundy rounded-full font-bold hover:bg-gray-100 transition-all"
              data-testid="pricing-final-cta"
            >
              Réserver mon essai gratuit
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
